// Product gallery: scroll-snap track with thumbnail and prev/next commands.
// Native scrolling owns swipe on touch; this component only syncs active state and handles commands.
class ProductGallery extends HTMLElement {
  #abortController = null;
  #observer = null;
  #index = 0;
  #track = null;
  #slides = [];
  #thumbs = [];
  #prev = null;
  #next = null;
  #counter = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#prev = this.querySelector('[data-gallery-prev]');
    this.#next = this.querySelector('[data-gallery-next]');
    this.#counter = this.querySelector('[data-gallery-counter]');
    if (!this.#track || this.#slides.length < 2) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('command', (event) => {
      if (event.command === '--prev') this.goTo(this.#index - 1);
      else if (event.command === '--next') this.goTo(this.#index + 1);
      else if (event.command === '--go') this.goTo(Number(event.source?.dataset.index));
    }, { signal });

    // Variant changes ask the gallery to show that variant's featured media.
    this.addEventListener('gallery:media', (event) => {
      const mediaId = String(event.detail?.mediaId || '');
      const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index >= 0) this.goTo(index, false);
    }, { signal });

    // Arrow keys move between thumbnails (roving focus within the strip).
    this.#thumbs.forEach((thumb, i) => {
      thumb.addEventListener('keydown', (event) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        let target = null;
        if (event.key === 'ArrowRight') target = rtl ? i - 1 : i + 1;
        else if (event.key === 'ArrowLeft') target = rtl ? i + 1 : i - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = this.#thumbs.length - 1;
        if (target === null) return;
        event.preventDefault();
        const clamped = Math.max(0, Math.min(this.#thumbs.length - 1, target));
        this.#thumbs[clamped].focus();
        this.goTo(clamped);
      }, { signal });
    });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target));
      }
    }, { root: this.#track, threshold: 0.6 });
    this.#slides.forEach((slide) => this.#observer.observe(slide));

    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#observer?.disconnect();
  }

  goTo(rawIndex, smooth = true) {
    if (!this.#track || Number.isNaN(rawIndex)) return;
    const index = Math.max(0, Math.min(this.#slides.length - 1, rawIndex));
    const slide = this.#slides[index];
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const left = rtl ? slide.offsetLeft + slide.offsetWidth - this.#track.clientWidth : slide.offsetLeft;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left, behavior: smooth && !reduceMotion ? 'smooth' : 'instant' });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    this.#slides.forEach((slide, i) => {
      // Off-screen slides leave the tab order so keyboard users only reach the visible image.
      slide.inert = i !== index;
    });
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    if (this.#prev) this.#prev.disabled = index === 0;
    if (this.#next) this.#next.disabled = index === this.#slides.length - 1;
    if (this.#counter) this.#counter.textContent = `${index + 1} / ${this.#slides.length}`;
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', ProductGallery);
